import { arrayMove } from '@dnd-kit/sortable'
import type { CustomStatisticResultItem, FieldValue, FilterFormType, SortableProtocol } from '@lighthouse/core'
import { type DataSourceAbstract, DataSourceType, type RecordLikeProtocol, SelectedMode } from '@lighthouse/core'
import {
    DEPARTMENT_DATASOURCE,
    getMainTableRecordId,
    getRealFieldId,
    getRecordKey,
    getRecordPoolKey,
    getWithScopeId,
    type ImportParams,
    normalizeUniqueIdValue,
    ROLE_DATASOURCE,
    USER_DATASOURCE
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import isDeepEqual from 'fast-deep-equal'
import { current } from 'immer'
import { atom } from 'jotai'
import { findIndex, omit } from 'rambda'

import * as srv from '@/services'
import type { GetRecordByFilterPayload } from '@/services/types'

import { applicationVersionAtom, currentAppIdAtom, currentEnvIdAtom } from '../application/state'
import { createDsRecordAction, deleteRecordAction, getFieldBeforeId } from './helper'
import {
    currentDataSourceIdAtom,
    dataSourceAtomFamily,
    dataSourceEnvAtom,
    // dataSourceEnvIdAtom,
    dataSourceListAtomFamily,
    dataSourcePoolAtom,
    dsDependentDataAtomFamily,
    recordAtomFamily,
    recordPoolAtom,
    viewIndependentDataAtomFamily
} from './state'
import type {
    AddDataSourceAtomPayload,
    AddRecordAtomPayload,
    CreateAggregationDataPayload,
    CreateAsyncDataSourcePayload,
    CreateDataSourcePayload,
    CreateFieldAtomPayload,
    CreateJoinDataSourcePayload,
    CreateRecordAtomPayload,
    DeleteDataSourceAtomPayload,
    DeleteFieldAtomPayload,
    DeleteRecordAtomPayload,
    DeleteRecordsAtomPayload,
    DeleteViewRecordAtomPayload,
    DuplicateDataSourcePayload,
    FetchDataSourceAtomPayload,
    FetchDataSourceListAtomPayload,
    GetDataSourceMetaAtomPayload,
    LoadDataSourceAtomPayload,
    MoveDataSourceAtomPayload,
    setBlockRecordAtomPayload,
    SetCurrentDataSourceIdAtomPayload,
    SetDataSourceAtomPayload,
    SetDataSourceIdsAtomPayload,
    SetRecordAtomPayload,
    SetRecordsAtomPayload,
    UpdateCellAtomPayload,
    UpdateDataSourceAtomPayload,
    UpdateDataSourceNameAtomPayload,
    UpdateFieldAtomPayload
} from './types'

/** 设置当前数据源id */
export const setCurrentDataSourceIdAtom = atom(null, (_, set, payload: SetCurrentDataSourceIdAtomPayload) => {
    const { dsId } = payload
    set(currentDataSourceIdAtom, dsId)
})

/** 设置数据源记录数据 */
export const setDsRecordAtom = atom(null, (_, set, payload: setBlockRecordAtomPayload) => {
    const { identifier, data } = payload
    const { dsId, viewId, scope } = identifier
    const withScopeId = getWithScopeId(viewId, scope)
    set(dsDependentDataAtomFamily({ dsId, viewId: withScopeId }), data)
})

/** 设置数据源列表 */
const setDataSourceListAtom = atom(null, (_, set, payload: SetDataSourceIdsAtomPayload) => {
    const { dataSourceList } = payload
    set(dataSourcePoolAtom, dataSourceList)
})

/** 获取应用下数据源列表 */
export const fetchDataSourceListAtom = atom(null, async (_, set, payload: FetchDataSourceListAtomPayload) => {
    const { appId, envId } = payload
    const data = await srv.listDs(appId, envId)
    set(setDataSourceListAtom, { dataSourceList: data })
    return data
})

/** 创建数据源 */
export const createDataSourceAtom = atom(null, async (_, set, payload: CreateDataSourcePayload) => {
    const { envId, type } = payload
    const data = await srv.createAndInitDs({ envId, type })
    if (data) {
        await set(fetchDataSourceListAtom, payload)
    }
    return data
})

/** 复制数据源 */
export const duplicateDataSourceAtom = atom(null, async (get, set, payload: DuplicateDataSourcePayload) => {
    const { envId, dsId } = payload
    const appId = get(currentAppIdAtom)
    const data = await srv.duplicateAndInitDs({ envId, dsId })
    if (data) {
        await set(fetchDataSourceListAtom, { appId, envId })
    }
    return data.dsId
})

/** 创建同步数据源 */
export const createAsyncDataSourceAtom = atom(null, async (get, set, payload: CreateAsyncDataSourcePayload) => {
    const { envId, syncAppId, syncEnvId, syncDsId } = payload
    const appId = get(currentAppIdAtom)
    const isCreate = await srv.createSyncDs({ envId, syncAppId, syncEnvId, syncDsId })
    if (isCreate) {
        set(fetchDataSourceListAtom, { appId, envId })
    }
    return isCreate
})

/** 创建同步数据源 */
export const createJoinDataSourceAtom = atom(null, async (get, set, payload: CreateJoinDataSourcePayload) => {
    const { envId, primaryDsId, config } = payload
    const appId = get(currentAppIdAtom)
    const data = await srv.createJoinDs({ envId, primaryDsId, config })
    if (data) {
        set(fetchDataSourceListAtom, { appId, envId })
    }
    return data
})

export const createAggregationDataSourceAtom = atom(null, async (get, set, payload: CreateAggregationDataPayload) => {
    const { envId } = payload
    const appId = get(currentAppIdAtom)
    const data = await srv.createAnalyticsDs(envId)
    if (data) {
        set(fetchDataSourceListAtom, { appId, envId })
    }
    return data
})

/** 导入数据源 */
export const importDataSourceAtom = atom(null, async (get, set, payload: ImportParams) => {
    const appId = get(currentAppIdAtom)
    const { envId } = payload
    const dataSources = await srv.importDataSource(payload)
    if (!dataSources || dataSources.length === 0) {
        return
    }
    if (dataSources?.length) {
        await set(fetchDataSourceListAtom, { appId, envId })
    }
    return dataSources.map(({ id }) => id)
})

/** 删除数据源 */
export const deleteDataSourceAtom = atom(null, async (get, set, payload: DeleteDataSourceAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId } = payload
    const list = get(dataSourcePoolAtom)
    set(dataSourcePoolAtom, draft => {
        const deleteIndex = findIndex(ds => ds.appId === appId && ds.envId === envId && ds.id === dsId, list)
        if (deleteIndex >= 0) {
            draft.splice(deleteIndex, 1)
        }
    })
    const isDelete = await srv.deleteDataSource(payload)
    if (!isDelete) {
        set(dataSourcePoolAtom, list)
        return
    }

    return isDelete
})

/** 更改数据源排序 */
export const moveDataSourceAtom = atom(null, async (get, set, payload: MoveDataSourceAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, oldIndex, newIndex } = payload
    const oldList = get(dataSourceListAtomFamily({ appId, envId }))
    const list = arrayMove(oldList, oldIndex, newIndex)
    const after = list?.[newIndex - 1]?.id
    const before = list?.[newIndex + 1]?.id
    set(setDataSourceListAtom, { dataSourceList: list })
    const isMove = await srv.moveDs({ envId, id: dsId, before, after })
    if (!isMove) {
        set(setDataSourceListAtom, { dataSourceList: oldList })
    }
})

/** 设置数据源数据 */
const setDataSourceAtom = atom(null, (get, set, payload: SetDataSourceAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, dataSource } = payload
    const dataSourceAtom = dataSourceAtomFamily({ appId, envId, dsId })
    set(dataSourceAtom, dataSource)
})

/** 添加单个数据源数据 */
const addDataSourceAtom = atom(null, (_, set, payload: AddDataSourceAtomPayload) => {
    const { dataSource } = payload
    set(dataSourcePoolAtom, draft => {
        const { id, appId, envId } = dataSource
        const index = draft.findIndex(item => item.id === id && item.appId === appId && item.envId === envId)
        if (index >= 0) {
            const originDataSource = current(draft[index])

            // TODO:HKL 后续处理合并问题
            if (isDeepEqual(originDataSource, dataSource)) {
                return
            }
            draft[index] = dataSource
        } else {
            draft.push(dataSource)
        }
    })
})

/** 设置记录数据 */
export const setRecordAtom = atom(null, (get, set, payload: SetRecordAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, recordId, record } = payload
    const recordAtom = recordAtomFamily({ appId, envId, dsId, recordId })
    set(recordAtom, record)
})

/** 设置记录数据（支持函数式更新或直接替换） */
export const setRecordsAtom = atom(null, (_, set, payload: SetRecordsAtomPayload) => {
    const { records } = payload
    if (typeof records === 'function') {
        set(recordPoolAtom, records)
    } else {
        set(recordPoolAtom, draft => {
            draft.clear()
            for (const record of records) {
                draft.set(getRecordKey(record), record)
            }
        })
    }
})

/** 删除指定数据源的记录 */
const deleteRecordsAtom = atom(null, (_, set, payload: DeleteRecordsAtomPayload) => {
    const { dsId } = payload
    set(recordPoolAtom, draft => {
        for (const [key, record] of draft.entries()) {
            if (record.dsId === dsId) {
                draft.delete(key)
            }
        }
    })
})

/** 添加记录数据 */
export const addRecordAtom = atom(null, (_, set, record: RecordLikeProtocol) => {
    set(recordPoolAtom, draft => {
        const key = getRecordKey(record)
        const mainRecordId = getMainTableRecordId(record.id)

        // 查找需要更新的关联记录（连接表场景）
        const relatedKeys: string[] = []
        for (const [k, item] of draft.entries()) {
            if (k === key) {
                continue
            }
            const isRelated =
                (getMainTableRecordId(item.id) === mainRecordId && item.appId === record.appId && item.dsId === record.dsId) ||
                (getMainTableRecordId(item.id) === mainRecordId &&
                    item.appId === record.appId &&
                    (item.primaryDsId === record.dsId || item.dsId === record.primaryDsId))
            if (isRelated) {
                relatedKeys.push(k)
            }
        }

        // 设置或更新主记录
        draft.set(key, record)

        // 更新关联记录的 content
        for (const relatedKey of relatedKeys) {
            const relatedRecord = draft.get(relatedKey)
            if (!relatedRecord?.id) {
                continue
            }
            const newContent = Object.entries(record.content).reduce<Record<string, FieldValue | undefined>>((acc, [fieldKey, value]) => {
                if (fieldKey.includes('__')) {
                    acc[fieldKey] = value
                } else {
                    acc[`${record.dsId}__${fieldKey}`] = value
                }
                return acc
            }, {})
            relatedRecord.content = { ...relatedRecord.content, ...newContent }
        }
    })
})

/** 添加多个记录数据 - O(n) 批量插入，无需遍历整个 pool */
export const addRecordsAtom = atom(null, (_, set, payload: AddRecordAtomPayload) => {
    const { records } = payload
    if (records.length === 0) {
        return
    }
    set(recordPoolAtom, draft => {
        for (const record of records) {
            draft.set(getRecordKey(record), record)
        }
    })
})

export const mergeRecordStatisticAtom = atom(
    null,
    (
        get,
        set,
        payload: {
            stackId: string
            dsId: string
            viewId: string
            recordIds: string[]
            uniqueIdField: string
            items: CustomStatisticResultItem[]
        }
    ) => {
        const { stackId, dsId, viewId, recordIds, uniqueIdField, items } = payload
        if (recordIds.length === 0 || items.length === 0) {
            return
        }
        const valueMap = new Map(items.map(item => [normalizeUniqueIdValue(item.uniqueIdValue), item.values]))
        const pool = get(recordPoolAtom)
        const customStatistic: Record<string, Record<string, number | null>> = {}
        for (const recordId of recordIds) {
            const record = pool.get(getRecordPoolKey(dsId, recordId))
            if (!record) {
                continue
            }
            const key = normalizeUniqueIdValue(record.content[uniqueIdField]?.value)
            const values = valueMap.get(key)
            if (!values) {
                continue
            }
            customStatistic[recordId] = values
        }
        if (Object.keys(customStatistic).length === 0) {
            return
        }
        set(viewIndependentDataAtomFamily({ stackId, dsId, viewId }), draft => {
            draft.customStatistic = customStatistic
        })
    }
)

export const getRecordByFilterAtom = atom(null, async (get, set, payload: GetRecordByFilterPayload) => {
    const { appId, envId, dsId } = payload
    const dataSource = get(dataSourceAtomFamily({ appId, envId, dsId }))
    const primaryDsId = dataSource?.type === DataSourceType.joinDataSource ? dataSource.viewOptions?.joinConfig?.primaryDsId : undefined
    const record = await srv.getRecordByFilter(payload)
    if (!record || !dataSource) {
        return record
    }
    const { schema } = dataSource
    const completeRecordContent = Object.entries(schema).reduce<Record<string, FieldValue | undefined>>((acc, [key, value]) => {
        const fieldValue = record.content[key]
        acc[key] = fieldValue
        return acc
    }, {})
    const recordWithPrimaryDsId: RecordLikeProtocol = { ...record, primaryDsId, content: completeRecordContent }
    set(addRecordAtom, recordWithPrimaryDsId)
    return recordWithPrimaryDsId
})

/** 获取数据源数据 */
export const fetchDataSourceAtom = atom(null, async (get, set, payload: FetchDataSourceAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, pagination, search, sorts, filter, linkFilter, abort, recordIds } = payload
    const data = await srv.getDs({
        appId,
        envId,
        dsId,
        pagination,
        search,
        filter,
        linkFilter,
        sorts,
        recordIds,
        abort
    })
    if (!data) {
        return
    }
    const { datasource, records } = data
    const ids = records.map(record => record.id)
    const rowTotal = await srv.getDsCount(payload)
    set(setDsRecordAtom, {
        identifier: { dsId, viewId: dsId },
        data: draft => {
            draft.recordPoints = ids.map(id => ({ id }))
            draft.currentPage = pagination.currentPage ?? 1
            draft.search = search
            draft.rowTotal = rowTotal
        }
    })

    set(addDataSourceAtom, { dataSource: datasource })
    set(addRecordsAtom, { records })
    return datasource
})

/** 加载数据源数据 */
export const loadDataSourceAtom = atom(null, async (get, set, payload: LoadDataSourceAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, pagination, search, sorts, filter, linkFilter, recordIds } = payload
    const data = await srv.getDs({
        appId,
        envId,
        dsId,
        pagination,
        search,
        filter,
        linkFilter,
        sorts,
        recordIds
    })
    if (!data) {
        return []
    }
    const { records } = data
    set(setDsRecordAtom, {
        identifier: { dsId, viewId: dsId },
        data: draft => {
            records.forEach(({ id }) => {
                draft.recordPoints.push({ id })
            })
            draft.currentPage = pagination.currentPage ?? 1
            draft.search = search
        }
    })
    set(addRecordsAtom, { records })
    return records
})

export const refreshDataSourceAtom = atom(null, async (get, set, payload: FetchDataSourceAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, pagination, search, filter, sorts, abort } = payload
    const data = await srv.getDs({
        appId,
        dsId,
        envId,
        pagination,
        search,
        filter,
        sorts,
        abort
    })
    if (!data) {
        return
    }
    const { datasource, records } = data
    const ids = records.map(record => record.id)
    set(addRecordsAtom, { records })
    set(setDsRecordAtom, {
        identifier: { dsId, viewId: dsId },
        data: draft => {
            draft.recordPoints = ids.map(id => ({ id }))
            draft.currentPage = pagination.currentPage ?? 1
            draft.search = search
        }
    })
    set(setDataSourceAtom, {
        envId,
        dsId,
        dataSource: datasource
    })
})

/** 更新数据源数据 */
export const updateDataSourceAtom = atom(null, async (get, set, payload: UpdateDataSourceAtomPayload) => {
    const { envId, dsId, params } = payload
    const appId = get(currentAppIdAtom)
    const dataSourceAtom = dataSourceAtomFamily({ appId, envId, dsId })
    const oldDataSource = get(dataSourceAtom)
    if (!oldDataSource) {
        return false
    }
    set(setDataSourceAtom, {
        envId,
        dsId,
        dataSource: draft => {
            if (draft) {
                Object.entries(params).forEach(([k, v]) => {
                    Reflect.set(draft, k, v)
                })
            }
        }
    })
    const { success } = await srv.updateDataSource({ id: dsId, envId, ...params })
    if (!success) {
        set(setDataSourceAtom, {
            envId,
            dsId,
            dataSource: oldDataSource
        })
    }
    return !!success
})

/** 更新数据源数据 */
export const updateDataSourceNameAtom = atom(null, async (get, set, payload: UpdateDataSourceNameAtomPayload) => {
    const { envId, dsId, name } = payload
    const appId = get(currentAppIdAtom)
    const dataSourceAtom = dataSourceAtomFamily({ appId, envId, dsId })
    const oldDataSource = get(dataSourceAtom)
    if (!oldDataSource) {
        return false
    }
    set(setDataSourceAtom, {
        envId,
        dsId,
        dataSource: draft => {
            if (draft) {
                draft.name = name
            }
        }
    })
    const { success, name: dsName } = await srv.updateDataSource({ id: dsId, envId, name })
    if (success && dsName !== name) {
        set(setDataSourceAtom, {
            envId,
            dsId,
            dataSource: draft => {
                if (draft) {
                    draft.name = dsName
                }
            }
        })
    }
    if (!success) {
        set(setDataSourceAtom, {
            envId,
            dsId,
            dataSource: oldDataSource
        })
    }
    return !!success
})

/** 创建字段 */
export const createFieldAtom = atom(null, async (get, set, payload: CreateFieldAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, config, sourceId, direction } = payload
    const { name, type, dsId: fieldDsId, dsName, innerType: fieldInnerType, ...rest } = config
    const dataSourceAtom = dataSourceAtomFamily({ appId, envId, dsId })
    const dataSource = get(dataSourceAtom)
    if (!dataSource) {
        return
    }
    const { viewOptions } = dataSource
    const { tableProps } = viewOptions
    const beforeId = getFieldBeforeId(tableProps, sourceId, direction)

    const { innerType, fieldId } = await srv.createField({
        envId,
        dsId,
        type,
        name,
        config: rest,
        // id: newFieldId,
        beforeId
    })
    if (!fieldId) {
        return
    }
    // const tableProp = { id: fieldId, visible: true, width: 160 }
    // let newTableProps = [...tableProps, tableProp]
    // if (sourceId && direction) {
    //     const index = findIndex(item => item.id === sourceId, tableProps)
    //     newTableProps = [...tableProps]
    //     newTableProps.splice(direction === 'left' ? index : index + 1, 0, tableProp)
    // }

    // set(setDataSourceAtom, {
    //     envId,
    //     dsId,
    //     dataSource: draft => {
    //         if (!draft) {
    //             return
    //         }
    //         draft.schema = { ...draft.schema, [fieldId]: { ...config, id: fieldId, innerType } }
    //         draft.viewOptions.tableProps = newTableProps
    //     }
    // })
    await set(fetchDataSourceListAtom, { appId, envId })
    return innerType
})

/** 更新字段 */
export const updateFieldAtom = atom(null, async (get, set, payload: UpdateFieldAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, fieldId, config } = payload
    const { id, name, type, dsId: fieldDsId, dsName, innerType: fieldInnerType, ...rest } = config
    const dataSourceAtom = dataSourceAtomFamily({ appId, envId, dsId })
    const oldDataSource = get(dataSourceAtom)
    if (!oldDataSource) {
        return false
    }

    const isUpdate = await srv.updateField({ envId, dsId, id: fieldId, config: rest, type, name })
    if (!isUpdate) {
        set(setDataSourceAtom, {
            envId,
            dsId,
            dataSource: oldDataSource
        })
        return false
    }
    await set(fetchDataSourceListAtom, { appId, envId })

    return isUpdate
})

/** 删除字段 */
export const deleteFieldAtom = atom(null, async (get, set, payload: DeleteFieldAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, fieldId } = payload
    const dataSourceAtom = dataSourceAtomFamily({ appId, envId, dsId })
    const oldDataSource = get(dataSourceAtom)
    if (!oldDataSource) {
        return false
    }
    set(setDataSourceAtom, {
        envId,
        dsId,
        dataSource: draft => {
            if (draft) {
                draft.schema = omit(fieldId, draft.schema)
                draft.viewOptions.tableProps = draft.viewOptions.tableProps.filter(item => item.id !== fieldId)
                if (draft.type === DataSourceType.joinDataSource && draft.viewOptions.joinConfig?.conditions) {
                    const realFieldId = getRealFieldId(fieldId)
                    draft.viewOptions.joinConfig.conditions.forEach(condition => {
                        const joinFieldIndex = findIndex(f => f.fieldId === realFieldId, condition.joinFields || [])
                        if (joinFieldIndex >= 0) {
                            condition.joinFields?.splice(joinFieldIndex, 1)
                        }
                    })
                }
            }
        }
    })

    const isDelete = await srv.deleteField({ envId, dsId, id: fieldId })
    if (!isDelete) {
        set(setDataSourceAtom, {
            envId,
            dsId,
            dataSource: oldDataSource
        })
        return false
    }

    return isDelete
})

/** 获取数据源配置数据 */
export const getDataSourceMetaAtom = atom(null, async (_, set, payload: GetDataSourceMetaAtomPayload) => {
    const dataSource = await srv.getDsMeta(payload)
    set(addDataSourceAtom, { dataSource })
    return dataSource
})

/** 创建记录数据 */
export const createRecordAtom = atom(null, async (get, set, payload: CreateRecordAtomPayload) => {
    const appId = get(currentAppIdAtom)
    const { recordId, envId, dsId, content, beforeId } = payload
    const dataSourceAtom = dataSourceAtomFamily({ appId, envId, dsId })
    const newRecordId = recordId || nanoid(32)
    const dataSource = get(dataSourceAtom)
    if (!dataSource) {
        return
    }
    const { schema } = dataSource
    createDsRecordAction({ id: newRecordId, envId, dsId, schema, beforeId }, get, set)
    const record = await srv.createRecord({ id: newRecordId, appId, envId, dsId, content, beforeId })
    if (!record) {
        deleteRecordAction({ recordIds: [newRecordId], envId, dsId, mode: SelectedMode.CURRENT_PAGE }, get, set)
        return
    }
    if (record) {
        set(setRecordAtom, {
            envId,
            dsId,
            recordId: newRecordId,
            record
        })
    }
    return record
})

/** 更新一条记录的多个字段（乐观更新） */
export const updateRecordAtom = atom(null, async (get, set, payload: Omit<RecordLikeProtocol, 'appId'>) => {
    const appId = get(currentAppIdAtom)
    const { envId, dsId, id, content } = payload
    const recordId = getMainTableRecordId(id)

    // 保存原始数据用于回滚
    const originalRecords = new Map<string, RecordLikeProtocol['content']>()
    const pool = get(recordPoolAtom)
    for (const record of pool.values()) {
        const isMatch =
            (record.appId === appId && record.envId === envId && record.dsId === dsId && getMainTableRecordId(record.id) === recordId) ||
            (getMainTableRecordId(record.id) === recordId && record.appId === appId && record.primaryDsId === dsId)
        if (isMatch) {
            // 只保存被修改字段的原始值
            const originalContent: RecordLikeProtocol['content'] = {}
            for (const k of Object.keys(content)) {
                originalContent[k] = record.content[k]
            }
            originalRecords.set(getRecordKey(record), originalContent)
        }
    }

    // 乐观更新：先更新本地状态
    set(recordPoolAtom, draft => {
        for (const record of draft.values()) {
            const isMatch =
                (record.appId === appId &&
                    record.envId === envId &&
                    record.dsId === dsId &&
                    getMainTableRecordId(record.id) === recordId) ||
                (getMainTableRecordId(record.id) === recordId && record.appId === appId && record.primaryDsId === dsId)
            if (isMatch) {
                for (const k of Object.keys(content)) {
                    record.content[k] = content[k]
                }
            }
        }
    })

    // 调用接口
    const backendFields = Object.keys(content).map(f => ({ fieldId: f, value: content[f]?.value }))
    const res = await srv.updateRecord({ appId, envId, dsId, recordId, fields: backendFields })

    // 如果失败，回滚到原始数据
    if (!res) {
        set(recordPoolAtom, draft => {
            for (const [key, originalContent] of originalRecords) {
                const record = draft.get(key)
                if (record) {
                    for (const k of Object.keys(originalContent)) {
                        record.content[k] = originalContent[k]
                    }
                }
            }
        })
    }

    return res
})

/** 删除记录数据 */
export const deleteRecordAtom = atom(null, async (get, set, payload: DeleteRecordAtomPayload) => {
    const { envId, dsId, viewId, recordIds, mode = SelectedMode.CURRENT_PAGE, search } = payload
    const isDelete = await srv.deleteRecord(payload)
    // TODO: HKL
    deleteRecordAction({ recordIds, viewId, envId, dsId, mode }, get, set)
    return isDelete
})

export const deleteViewRecordAtom = atom(null, async (get, set, payload: DeleteViewRecordAtomPayload) => {
    const { envId, dsId, scope, viewId, recordIds, mode } = payload
    let isDeleted = false
    if (mode === 'CURRENT_PAGE' && recordIds) {
        isDeleted = await srv.deleteRecord({ envId, dsId, recordIds, mode })
        if (isDeleted) {
            set(setDsRecordAtom, {
                identifier: { dsId, viewId, scope },
                data: draft => {
                    const deleteIds = new Set(recordIds)
                    draft.recordPoints = draft.recordPoints.filter(point => !deleteIds.has(point.id))
                    if (draft.rowTotal || draft.rowTotal === 0) {
                        draft.rowTotal -= recordIds.length
                    }
                }
            })
        }
    } else {
        isDeleted = await srv.deleteViewRecord(payload)
        set(setDsRecordAtom, {
            identifier: { dsId, viewId, scope },
            data: draft => {
                draft.recordPoints = []
                draft.rowTotal = 0
            }
        })
    }
    return isDeleted
})

/** 更新单元格 */
export const updateCellAtom = atom(null, async (_, set, payload: UpdateCellAtomPayload, options?: { local?: boolean }) => {
    const { recordId, envId, dsId, fieldId, value } = payload
    let oldRecord: RecordLikeProtocol | undefined
    set(setRecordAtom, {
        dsId,
        envId,
        recordId,
        record: draft => {
            if (draft) {
                oldRecord = current(draft)
                draft.content[fieldId] = value
            }
        }
    })
    if (options?.local) {
        return true
    }
    const content = {
        value: value.value
    }
    const isUpdate = await srv.updateCell({
        envId,
        dsId,
        id: recordId,
        fieldId,
        content
    })

    if (!isUpdate && oldRecord) {
        set(setRecordAtom, {
            dsId,
            envId,
            recordId,
            record: oldRecord
        })
    }
    return isUpdate
})

/** 设置数据源环境id */
export const setDataSourceEnvIdAtom = atom(null, (_, set, id: string) => {
    // set(dataSourceEnvIdAtom, id)
    set(applicationVersionAtom, { envId: id })
})

/** 获取环境数据 */
export const fetchEnvAtom = atom(null, async (get, set) => {
    // const envId = get(dataSourceEnvIdAtom)
    const envId = get(currentEnvIdAtom)
    const data = await srv.getEnv(envId)
    set(dataSourceEnvAtom, data)
    return data
})

const fetchDataSourceRecordsAtom = atom(null, async (get, set, dsId: string) => {
    const appId = get(currentAppIdAtom)
    const envId = get(currentEnvIdAtom)
    const pagination = {
        currentPage: 1,
        pageSize: 30000
    }
    const data = await srv.getDs({
        appId,
        envId,
        dsId,
        pagination
    })
    if (!data) {
        return
    }
    const { records, datasource } = data
    const recordPoints = records.map(item => ({ id: item.id }))
    set(setDsRecordAtom, {
        identifier: { dsId, viewId: dsId },
        data: draft => {
            draft.rowTotal = records.length
            draft.recordPoints = recordPoints
            draft.currentPage = pagination.currentPage ?? 1
        }
    })
    set(addDataSourceAtom, { dataSource: datasource })
    set(deleteRecordsAtom, { dsId })
    set(addRecordsAtom, { records })
    return records
})

/** 获取数据源下用户列表 */
export const fetchDataSourceUserAtom = atom(null, (get, set) => {
    return set(fetchDataSourceRecordsAtom, USER_DATASOURCE)
})

/** 获取数据源下用户组列表 */
export const fetchDataSourceRoleAtom = atom(null, (get, set) => {
    return set(fetchDataSourceRecordsAtom, ROLE_DATASOURCE)
})

/** 获取数据源下部门列表 */
export const fetchDataSourceDepartmentAtom = atom(null, (get, set) => {
    return set(fetchDataSourceRecordsAtom, DEPARTMENT_DATASOURCE)
})

export const fetchSystemDataSourceAtom = atom(null, (get, set) => {
    set(fetchDataSourceRecordsAtom, USER_DATASOURCE)
    set(fetchDataSourceRecordsAtom, DEPARTMENT_DATASOURCE)
    set(fetchDataSourceRecordsAtom, ROLE_DATASOURCE)
})

/** 获取表单record */
export const fetchFormRecordAtom = atom(null, (get, set, payload: { dsId: string; filter?: FilterFormType }) => {
    const { dsId, filter } = payload
    const appId = get(currentAppIdAtom)
    const envId = get(currentEnvIdAtom)
    return set(getRecordByFilterAtom, {
        appId,
        envId,
        dsId,
        filter
    })
})

/** 获取字段组record */
export const fetchFieldGroupRecordAtom = atom(
    null,
    (get, set, payload: { dsId: string; sorts: SortableProtocol['sorts']; filter?: FilterFormType }) => {
        const { dsId, filter, sorts } = payload
        const appId = get(currentAppIdAtom)
        const envId = get(currentEnvIdAtom)
        return set(getRecordByFilterAtom, {
            appId,
            envId,
            dsId,
            filter,
            sorts
        })
    }
)
